import React, { PropsWithChildren, useMemo } from 'react'
import { CssBaseline, StyledEngineProvider, ThemeOptions, ThemeProvider, createTheme } from '@mui/material'
import { useSettingsContext } from '@app/layout/settings'
import merge from 'lodash/merge'
import { customShadows } from './custom-shadows'
import { contrast } from './options/contrast'
import { darkMode } from './options/dark-mode'
import { presets } from './options/presets'
import { componentsOverrides } from './overrides'
import { palette } from './palette'
import { shadows } from './shadows'
import { typography } from './typography'

const MuiThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const settings = useSettingsContext()
  const darkModeOption = darkMode(settings.themeMode)

  const presetsOption = presets(settings.themeColorPresets || 'default')

  const contrastOption = contrast(settings.themeContrast === 'bold', settings.themeMode)

  const baseOption = useMemo(
    () => ({
      palette: palette('light'),
      shadows: shadows('light'),
      customShadows: customShadows('light'),
      typography,
      shape: { borderRadius: 5 }
    }),
    []
  )

  const memoizedValue = useMemo(
    () =>
      merge(
        // Base
        baseOption,
        // // Direction: remove if not in use
        // directionOption,
        // Dark mode: remove if not in use
        darkModeOption,
        // Presets: remove if not in use
        presetsOption,
        // Contrast: remove if not in use
        contrastOption.theme
      ),
    [baseOption, darkModeOption, presetsOption, contrastOption.theme]
  )

  const theme = createTheme(memoizedValue as ThemeOptions)

  theme.components = merge(componentsOverrides(theme), contrastOption.components)

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default MuiThemeProvider
